// Resolve a stored media URL for playback in the browser AND inside
// the Capacitor Android WebView.
//
// Background: `capacitor.config.json` sets `androidScheme: "https"`,
// so on Android the app is served from `https://localhost/`. A relative
// URL like `/api/files/voice-note.webm` therefore resolves to
// `https://localhost/api/files/...` (the WebView itself) instead of the
// Yorm backend. Images already handle this inline; audio elements did
// not, which is why voice notes silently failed to play on Android.
//
// If the incoming URL already includes a scheme (Firebase Storage
// public URLs, external CDNs) we leave it untouched.
//
// AUDIO CACHE-BUSTER: legacy `.webm` / `.ogg` voice notes uploaded
// before the server-side transcoder was live are still cached by
// Cloudflare edge as `content-type: audio/webm` — a MIME type iOS
// Safari refuses to decode. Even after we deploy the transcoder,
// Cloudflare keeps serving the stale webm for up to 24 h until TTL
// expires. Appending `?v=<version>` creates a NEW cache key so the
// request misses the edge, reaches the origin, and is transcoded to
// m4a on the fly.
//
// ⚠ Bump this version string whenever a serve-side audio fix ships.
// The previous `m4a2` and `m4a3` values were cached by Cloudflare
// before the `no-store` Cache-Control landed on the backend AND before
// we fixed the VoiceRecorder init-segment bug (see VoiceRecorder.jsx),
// so a bump is required to force a fresh miss.
const AUDIO_CACHE_BUSTER_VERSION = "m4a5";
const LEGACY_AUDIO_EXT_RE = /\.(webm|ogg|flac)(?:$|\?)/i;

export const resolveMediaUrl = (url, apiBase) => {
  if (!url) return url;
  if (typeof url !== "string") return url;
  let out = url;
  if (out.startsWith("blob:") || out.startsWith("data:")) return out;
  if (!(out.startsWith("http://") || out.startsWith("https://"))) {
    if (out.startsWith("/")) {
      const origin = (apiBase || "").replace(/\/api\/?$/, "");
      out = `${origin}${out}`;
    } else {
      return out;
    }
  }
  // Cache-buster only for legacy audio extensions — image / video URLs
  // stay clean so Cloudflare can still cache them normally.
  if (LEGACY_AUDIO_EXT_RE.test(out) && !out.includes("v=" + AUDIO_CACHE_BUSTER_VERSION)) {
    out += (out.includes("?") ? "&" : "?") + "v=" + AUDIO_CACHE_BUSTER_VERSION;
  }
  return out;
};
